import { useState, type FormEvent } from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import { Section } from '../components/section';
import { CONTACT_EMAIL } from '../lib/config';

interface FormState {
  schoolName: string;
  contactName: string;
  email: string;
  phone: string;
  studentsEnrolled: string;
  message: string;
  wantsDemo: boolean;
}

const INITIAL: FormState = {
  schoolName: '',
  contactName: '',
  email: '',
  phone: '',
  studentsEnrolled: '',
  message: '',
  wantsDemo: true,
};

export function ContactPage() {
  const [form, setForm] = useState<FormState>(INITIAL);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const subject = encodeURIComponent(
      form.wantsDemo
        ? `Demo request — ${form.schoolName || 'School'}`
        : `Enquiry — ${form.schoolName || 'School'}`,
    );
    const lines: string[] = [
      `School: ${form.schoolName}`,
      `Contact: ${form.contactName}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Students enrolled: ${form.studentsEnrolled}`,
      `Demo requested: ${form.wantsDemo ? 'Yes' : 'No'}`,
      '',
      'Message:',
      form.message || '(none)',
    ];
    const body = encodeURIComponent(lines.join('\n'));
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  }

  return (
    <>
      <header className="mx-auto max-w-6xl px-6 pb-10 pt-20 md:pt-24">
        <div className="max-w-2xl">
          <p
            className="text-[11px] font-semibold uppercase"
            style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
          >
            Contact
          </p>
          <h1
            className="mt-3"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3.25rem)',
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: '-0.032em',
            }}
          >
            Tell us a little about your school.
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--fg-mid)' }}>
            We&apos;ll get back within one working day with a demo or a written reply, whichever
            suits you. Nothing here is sent to a CRM — it goes to a human.
          </p>
        </div>
      </header>

      <Section>
        <div className="grid gap-10 md:grid-cols-3 md:gap-14">
          <aside className="md:col-span-1">
            <p
              className="text-[11px] font-semibold uppercase"
              style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
            >
              Other ways to reach us
            </p>
            <ul className="mt-5 space-y-5">
              <li className="flex items-start gap-3">
                <Mail
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                  style={{ color: 'var(--color-accent-600)' }}
                />
                <div>
                  <p
                    className="text-[12px] uppercase tracking-wider"
                    style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
                  >
                    Email
                  </p>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="mt-0.5 inline-block text-[14px] font-medium"
                    style={{ color: 'var(--fg-high)' }}
                  >
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                  style={{ color: 'var(--color-accent-600)' }}
                />
                <div>
                  <p
                    className="text-[12px] uppercase tracking-wider"
                    style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
                  >
                    Phone
                  </p>
                  <p className="mt-0.5 text-[14px] font-medium" style={{ color: 'var(--fg-high)' }}>
                    +91 9876 543 210
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                  style={{ color: 'var(--color-accent-600)' }}
                />
                <div>
                  <p
                    className="text-[12px] uppercase tracking-wider"
                    style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
                  >
                    Office
                  </p>
                  <p className="mt-0.5 text-[14px] font-medium" style={{ color: 'var(--fg-high)' }}>
                    Surandai, Tamil Nadu, India
                  </p>
                </div>
              </li>
            </ul>
          </aside>

          <form
            onSubmit={handleSubmit}
            className="md:col-span-2"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-line)',
              borderRadius: 'var(--radius-xl)',
              padding: '28px',
              boxShadow: 'var(--elev-1)',
            }}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="School name" required>
                <input
                  className="ed-input"
                  value={form.schoolName}
                  onChange={(e) => update('schoolName', e.target.value)}
                  placeholder="e.g. SR School of Excellence"
                  required
                />
              </Field>
              <Field label="Contact person" required>
                <input
                  className="ed-input"
                  value={form.contactName}
                  onChange={(e) => update('contactName', e.target.value)}
                  placeholder="e.g. Anitha S."
                  required
                />
              </Field>
              <Field label="Email" required>
                <input
                  className="ed-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="you@school.in"
                  required
                />
              </Field>
              <Field label="Phone">
                <input
                  className="ed-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="+91 ..."
                />
              </Field>
              <Field label="Students enrolled">
                <input
                  className="ed-input"
                  type="number"
                  min={0}
                  step={1}
                  value={form.studentsEnrolled}
                  onChange={(e) => update('studentsEnrolled', e.target.value)}
                  placeholder="e.g. 480"
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="How can we help?">
                <textarea
                  className="ed-textarea"
                  rows={5}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder="Tell us about your current system, what's working, what isn't."
                />
              </Field>
            </div>

            <label
              className="mt-5 flex items-start gap-3 rounded-lg p-3"
              style={{
                background: 'var(--bg-sunken)',
                border: '1px solid var(--border-hair)',
              }}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer rounded"
                style={{ accentColor: 'var(--color-accent-500)' }}
                checked={form.wantsDemo}
                onChange={(e) => update('wantsDemo', e.target.checked)}
              />
              <span>
                <span
                  className="block text-[13px] font-medium"
                  style={{ color: 'var(--fg-high)' }}
                >
                  I&apos;d like a demo
                </span>
                <span
                  className="mt-0.5 block text-[12px]"
                  style={{ color: 'var(--fg-low)' }}
                >
                  We&apos;ll suggest 2–3 time slots within 24 hours.
                </span>
              </span>
            </label>

            <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[12px]" style={{ color: 'var(--fg-low)' }}>
                We respect your inbox — never shared, never sold.
              </p>
              <button type="submit" className="ed-btn-primary">
                Send message
              </button>
            </div>
          </form>
        </div>
      </Section>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="ed-label">
        {label}
        {required && (
          <span style={{ color: 'var(--semantic-danger)' }} aria-hidden>
            {' '}
            *
          </span>
        )}
      </span>
      {children}
    </label>
  );
}
